<template>
  <!--
    좌측 레일의 업무 바로가기 (데스크톱 ≥1024px 에서만 — SideNav 의 .snav__wide 안).
    그룹 머리글을 두지 않는다. 11개에 머리글 4개가 붙으면 레일이 미니 캘린더를
    화면 밖으로 밀어낸다. 그룹은 타일 색이 대신 말한다(utils/quickLinks.js).
    접어 둘 수 있고, 그 상태는 이 브라우저에 기억한다.
  -->
  <div class="qlr">
    <div class="qlr__head">
      <button
        type="button"
        class="qlr__toggle"
        :aria-expanded="open"
        @click="toggle"
      >
        <el-icon class="qlr__caret" :class="{ 'is-open': open }"><ArrowRight /></el-icon>
        <span>바로가기</span>
      </button>
      <button type="button" class="qlr__all" @click="$router.push('/links')">전체</button>
    </div>

    <ul v-show="open" class="qlr__list">
      <li v-for="l in ordered" :key="l.id">
        <a class="qlr__row" :href="l.url" target="_blank" rel="noopener noreferrer" :title="`${l.name}${l.note ? ' · ' + l.note : ''}`">
          <span class="qlr__icon" :class="`qlr__icon--${tone(l.groupName)}`" aria-hidden="true">{{ initial(l) }}</span>
          <span class="qlr__name u-trunc">{{ l.name }}</span>
          <el-icon class="qlr__ext"><TopRight /></el-icon>
        </a>
      </li>
      <li v-if="loaded && !ordered.length" class="qlr__empty">등록된 바로가기가 없습니다</li>
    </ul>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { ArrowRight, TopRight } from '@element-plus/icons-vue'
import { groupLinks, linkInitial, linkTone } from '@/utils/quickLinks'

const KEY = 'snav_links_open'

export default {
  name: 'QuickLinksRail',
  components: { ArrowRight, TopRight },
  setup() {
    const store = useStore()
    let saved = null
    try { saved = localStorage.getItem(KEY) } catch (e) { /* 사생활 보호 모드 */ }
    const open = ref(saved !== '0')

    const loaded = computed(() => store.state.quickLinksLoaded)
    // 그룹 순서 → 그룹 안 순서로 한 줄로 편다. 같은 색이 이웃해야 묶음으로 보인다.
    const ordered = computed(() => groupLinks(store.state.quickLinks).flatMap((g) => g.links))

    function toggle() {
      open.value = !open.value
      try { localStorage.setItem(KEY, open.value ? '1' : '0') } catch (e) { /* noop */ }
    }

    onMounted(() => {
      store.dispatch('fetchQuickLinks').catch(() => {})
    })

    return { open, loaded, ordered, toggle, initial: linkInitial, tone: linkTone }
  }
}
</script>

<style scoped>
.qlr { padding: 2px var(--sp-3) var(--sp-2); }

.qlr__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--sp-1) var(--sp-1);
}

.qlr__toggle,
.qlr__all {
  border: 0;
  background: none;
  font-family: inherit;
  cursor: pointer;
  border-radius: var(--r-sm);
}

.qlr__toggle {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-1);
  padding: var(--sp-1) 2px;
  font-size: var(--fs-2xs);
  font-weight: var(--fw-semibold);
  letter-spacing: 0.09em;
  color: var(--color-ink-text-dim);
}
.qlr__toggle:hover { color: var(--color-ink-text-bright); }

.qlr__caret { font-size: var(--ic-xs); transition: transform var(--dur-fast) var(--ease); }
.qlr__caret.is-open { transform: rotate(90deg); }

.qlr__all {
  padding: var(--sp-1) var(--sp-2);
  font-size: var(--fs-xs);
  color: var(--color-ink-text-dim);
}
.qlr__all:hover { background: var(--color-ink-2); color: var(--color-on-accent); }

.qlr__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.qlr__row {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 32px;
  padding: 0 var(--sp-2) 0 6px;
  border-radius: var(--r-md);
  color: var(--color-ink-text);
  text-decoration: none;
  font-size: var(--fs-sm);
  transition: background var(--dur-fast) var(--ease), color var(--dur-fast) var(--ease);
}
.qlr__row:hover { background: var(--color-ink-2); color: var(--color-on-accent); }

.qlr__icon {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  border-radius: var(--r-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--fs-xs);
  font-weight: var(--fw-bold);
}
.qlr__icon--primary { background: var(--color-primary-soft); color: var(--color-primary-strong); }
.qlr__icon--success { background: var(--color-success-soft); color: var(--color-success-strong); }
.qlr__icon--warning { background: var(--color-warning-soft); color: var(--color-warning-strong); }
.qlr__icon--violet { background: var(--evt-fieldwork-bg); color: var(--evt-fieldwork-fg); }
.qlr__icon--muted { background: var(--color-ink-line-2); color: var(--color-ink-text-bright); }

.qlr__name { flex: 1; }

/* 새 탭으로 열린다는 표시. 평소엔 숨겨 두고 가리킬 때만 — 11줄에 화살표가 다 붙으면 잡음이다. */
.qlr__ext { font-size: var(--ic-xs); color: var(--color-ink-text-faint); opacity: 0; transition: opacity var(--dur-fast) var(--ease); }
.qlr__row:hover .qlr__ext,
.qlr__row:focus-visible .qlr__ext { opacity: 1; }

.qlr__empty { padding: var(--sp-1) var(--sp-2); font-size: var(--fs-xs); color: var(--color-ink-text-faint); }
</style>
